/**
 * Question validation utilities for Question Bank Review page
 * Type-specific validation rules for all 9 question types
 */

export const validateQuestion = (question) => {
  const errors = {};

  // ===== COMMON VALIDATIONS =====
  const plainText = (question.question_text || "").replace(/<[^>]*>/g, "").trim();
  const hasImage = question.image_url || question.image_urls?.length > 0;
  const hasOverlayImage = !!question.answer_data?.overlay_layout?.image_url?.trim();
  const hasTableLayout =
    question.type === "table" && (question.answer_data?.table_layout?.grid?.length || 0) > 0;
  if (!plainText && !hasImage && !hasOverlayImage && !hasTableLayout) {
    errors.question_text = "Question text or an image is required";
  }

  if (!question.type) {
    errors.type = "Question type is required";
  }

  if (!question.difficulty || !["Easy", "Medium", "Hard"].includes(question.difficulty)) {
    errors.difficulty = "Valid difficulty level is required";
  }

  if (!question.default_marks || question.default_marks < 1) {
    errors.default_marks = "Marks must be at least 1";
  }

  // ===== TYPE-SPECIFIC VALIDATIONS =====
  switch (question.type) {
    case "multiple-choice":
      validateMultipleChoice(question, errors);
      break;

    case "multiple-select":
      validateMultipleSelect(question, errors);
      break;

    case "true-false":
      validateTrueFalse(question, errors);
      break;

    case "fill-in-the-blank":
      validateFillInTheBlank(question, errors);
      break;

    case "multi-fill":
      validateMultiFill(question, errors);
      break;

    case "table":
      validateTable(question, errors);
      break;

    case "image-fill":
      validateImageFill(question, errors);
      break;

    case "grouped":
      break;

    case "short-answer":
      validateShortAnswer(question, errors);
      break;

    case "essay":
      validateEssay(question, errors);
      break;

    case "matching":
      validateMatching(question, errors);
      break;

    case "ordering":
      validateOrdering(question, errors);
      break;

    case "drawing":
      // Drawing answers are graded from an interactive geometry spec or AI vision — no text/option answer required.
      break;

    default:
      errors.type = "Unknown question type";
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors,
  };
};

// ===== INDIVIDUAL TYPE VALIDATORS =====

const validateMultipleChoice = (question, errors) => {
  if (!question.options || question.options.length < 2) {
    errors.options = "At least 2 options required";
    return;
  }

  if (question.options.length > 10) {
    errors.options = "Maximum 10 options allowed";
    return;
  }

  // Check for empty option text
  const emptyOptionIdx = question.options.findIndex((o) => !o.text?.trim());
  if (emptyOptionIdx >= 0) {
    errors.options = `Option ${emptyOptionIdx + 1} text cannot be empty`;
    return;
  }

  // Check for correct answer(s)
  const correctOptions = question.options.filter((o) => o.is_correct);
  if (correctOptions.length === 0) {
    errors.options = "At least one correct answer required";
    return;
  }

  if (correctOptions.length > 1) {
    errors.options = "Only one correct answer allowed for multiple-choice";
  }
};

const validateMultipleSelect = (question, errors) => {
  if (!question.options || question.options.length < 2) {
    errors.options = "At least 2 options required";
    return;
  }

  if (question.options.length > 10) {
    errors.options = "Maximum 10 options allowed";
    return;
  }

  // Check for empty option text
  const emptyOptionIdx = question.options.findIndex((o) => !o.text?.trim());
  if (emptyOptionIdx >= 0) {
    errors.options = `Option ${emptyOptionIdx + 1} text cannot be empty`;
    return;
  }

  // Check for correct answers
  const correctOptions = question.options.filter((o) => o.is_correct);
  if (correctOptions.length === 0) {
    errors.options = "At least one correct answer required";
  }
};

const validateTrueFalse = (question, errors) => {
  if (!question.options || question.options.length !== 2) {
    errors.options = "Exactly 2 options required for true/false";
    return;
  }

  // Check for empty option text
  const emptyOptionIdx = question.options.findIndex((o) => !o.text?.trim());
  if (emptyOptionIdx >= 0) {
    errors.options = `Option ${emptyOptionIdx + 1} text cannot be empty`;
    return;
  }

  // Check for correct answer
  const correctOptions = question.options.filter((o) => o.is_correct);
  if (correctOptions.length !== 1) {
    errors.options = "Exactly one correct answer required";
  }
};

const validateFillInTheBlank = (question, errors) => {
  const blanks = question.answer_data?.correct_blanks || [];

  if (blanks.length === 0) {
    errors.answer_data = "At least one blank required";
    return;
  }

  // Check each blank has at least one answer
  for (let i = 0; i < blanks.length; i++) {
    const blank = blanks[i];
    if (!blank || blank.length === 0 || !blank[0]?.trim()) {
      errors.answer_data = `Blank ${i + 1} must have at least one accepted answer`;
      return;
    }

    // Check all answers in the blank are non-empty
    const emptyAnswerIdx = blank.findIndex((ans) => !ans?.trim());
    if (emptyAnswerIdx >= 0) {
      errors.answer_data = `Blank ${i + 1}, answer ${emptyAnswerIdx + 1} cannot be empty`;
      return;
    }
  }
};

const validateMultiFill = (question, errors) => {
  const blanks = question.answer_data?.correct_blanks || [];

  if (blanks.length < 2) {
    errors.answer_data = "At least 2 blanks required for multi-fill";
    return;
  }

  // Check each blank has at least one answer
  for (let i = 0; i < blanks.length; i++) {
    const blank = blanks[i];
    if (!blank || blank.length === 0 || !blank[0]?.trim()) {
      errors.answer_data = `Blank ${i + 1} must have at least one accepted answer`;
      return;
    }

    // Check all answers in the blank are non-empty
    const emptyAnswerIdx = blank.findIndex((ans) => !ans?.trim());
    if (emptyAnswerIdx >= 0) {
      errors.answer_data = `Blank ${i + 1}, answer ${emptyAnswerIdx + 1} cannot be empty`;
      return;
    }
  }
};

const validateImageFill = (question, errors) => {
  const blanks = question.answer_data?.correct_blanks || [];
  const overlay = question.answer_data?.overlay_layout;
  const mode = overlay?.mode === "inline" ? "inline" : "boxes";

  if (mode === "boxes" && !overlay?.image_url?.trim()) {
    errors.answer_data = "Background image is required for box-mode image fill";
    return;
  }

  if (blanks.length === 0) {
    errors.answer_data = "At least one answer box is required";
    return;
  }

  const hasAnyFilledBlank = blanks.some(
    (blank) => Array.isArray(blank) && blank.some((ans) => String(ans ?? "").trim())
  );
  if (!hasAnyFilledBlank) {
    errors.answer_data = "At least one box must have an accepted answer";
    return;
  }

  for (let i = 0; i < blanks.length; i++) {
    const blank = blanks[i];
    if (!Array.isArray(blank)) continue;
    const emptyAnswerIdx = blank.findIndex(
      (ans) => ans != null && ans !== "" && !String(ans).trim()
    );
    if (emptyAnswerIdx >= 0) {
      errors.answer_data = `Box ${i + 1}, answer ${emptyAnswerIdx + 1} cannot be empty`;
      return;
    }
  }
};

const validateTable = (question, errors) => {
  const blanks = question.answer_data?.correct_blanks || [];
  const layout = question.answer_data?.table_layout;

  if (!layout?.grid?.length) {
    errors.answer_data = "Table layout is required";
    return;
  }

  if (blanks.length < 1) {
    errors.answer_data = "At least 1 editable table cell answer is required";
    return;
  }

  for (let i = 0; i < blanks.length; i++) {
    const blank = blanks[i];
    if (!blank || blank.length === 0 || !blank[0]?.trim()) {
      errors.answer_data = `Table cell ${i + 1} must have at least one accepted answer`;
      return;
    }

    const emptyAnswerIdx = blank.findIndex((ans) => !ans?.trim());
    if (emptyAnswerIdx >= 0) {
      errors.answer_data = `Table cell ${i + 1}, answer ${emptyAnswerIdx + 1} cannot be empty`;
      return;
    }
  }
};

const validateShortAnswer = (question, errors) => {
  // correct_answer_text is optional — comprehension and open-ended questions
  // are marked by teachers and do not require a stored model answer.
};

const validateEssay = (question, errors) => {
  // correct_answer_text is optional — essay and creative-writing questions
  // are marked by teachers and do not require a stored model answer.
};

const validateMatching = (question, errors) => {
  if (!question.options || question.options.length < 2) {
    errors.options = "At least 2 matching pairs required";
    return;
  }

  if (question.options.length > 10) {
    errors.options = "Maximum 10 pairs allowed";
    return;
  }

  // Check each pair is complete
  for (let i = 0; i < question.options.length; i++) {
    const pair = question.options[i];
    if (!pair.text?.trim()) {
      errors.options = `Pair ${i + 1}: Left side cannot be empty`;
      return;
    }
    if (!pair.match_text?.trim()) {
      errors.options = `Pair ${i + 1}: Right side cannot be empty`;
      return;
    }
  }
};

const validateOrdering = (question, errors) => {
  if (!question.options || question.options.length < 2) {
    errors.options = "At least 2 items required for ordering";
    return;
  }

  if (question.options.length > 10) {
    errors.options = "Maximum 10 items allowed";
    return;
  }

  // Check each item has text
  for (let i = 0; i < question.options.length; i++) {
    const item = question.options[i];
    if (!item.text?.trim()) {
      errors.options = `Item ${i + 1} cannot be empty`;
      return;
    }
  }
};

/**
 * Get human-friendly error message for a field
 */
export const getErrorMessage = (errors, field) => {
  return errors[field] || "";
};

/**
 * Check if a field has validation error
 */
export const hasFieldError = (errors, field) => {
  return !!errors[field];
};
